import React, { useState, useEffect } from 'react';
import { getDomainConfig, siteConfig } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    // Get domain-specific config on mount
    setConfig(getDomainConfig());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    
    // Simulate API call (will be replaced with backend)
    setTimeout(() => {
      setIsSubmitted(true);
      setIsLoading(false);
      toast.success(siteConfig.successMessage);
      setEmail('');
    }, 800);
  };

  // Show loading state while config loads
  if (!config) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Noise texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-0" 
           style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} 
      />

      {/* Header */}
      <header className="relative z-10 w-full px-6 md:px-12 lg:px-20 py-6">
        <nav className="flex items-center justify-between">
          <div className="logo">
            <span className="text-lg md:text-xl font-black tracking-tight text-black">
              {config.siteName}
            </span>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center">
        <div className="w-full px-6 md:px-12 lg:px-20 py-12 md:py-20">
          <div className="max-w-4xl">
            {/* Tagline */}
            <p className="text-xs md:text-sm tracking-[0.3em] text-gray-500 mb-6 font-medium">
              {config.tagline}
            </p>

            {/* Main Headline */}
            <h1 className="text-[4rem] md:text-[7rem] lg:text-[9rem] font-black leading-[0.85] tracking-tighter text-black mb-8">
              COMING<br />SOON
            </h1>

            {/* Description */}
            <p className="text-base md:text-lg text-gray-600 max-w-xl mb-4 leading-relaxed">
              {config.description}
            </p>
            <p className="text-base md:text-lg text-gray-600 max-w-xl mb-10 leading-relaxed">
              {config.launchMessage}
            </p>

            {/* Email Signup Form */}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mb-12">
              <Input
                type="email"
                placeholder={config.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-14 px-5 text-base border-2 border-black bg-white text-black placeholder:text-gray-400 focus:ring-0 focus:border-black rounded-none"
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="h-14 px-8 bg-white text-black border-2 border-black hover:bg-black hover:text-white transition-colors duration-300 rounded-none font-bold tracking-wide text-sm"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    SENDING...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {config.buttonText}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            {/* Success Message */}
            {isSubmitted && (
              <div className="mb-8 animate-fade-in">
                <p className="text-sm text-gray-600 tracking-wide">
                  ✓ {config.successMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full px-6 md:px-12 lg:px-20 py-6">
        <p className="text-xs text-gray-400 tracking-wide flex items-center gap-2">
          {config.footerText}
          <svg 
            className="w-5 h-4 inline-block" 
            viewBox="0 0 32 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="32" height="24" fill="#DA291C" rx="2"/>
            <path d="M14 7h4v4h4v4h-4v4h-4v-4h-4v-4h4V7z" fill="white"/>
          </svg>
        </p>
      </footer>
    </div>
  );
};

export default ComingSoon;
